// frontend/src/tabs/TabStrip.jsx
//
// The tab strip. Renders the open tabs above the main content, handles
// focus / close clicks, drag-to-reorder, and syncs the browser URL to the
// active tab. The cap-reached message is AppToast's (capEvent.js).
//
// Why URL sync lives here: the strip is the single place that wraps the
// router-driven URL state and the tabs-driven active id together. The
// activeId drives rendering through <TabsHost/>; the strip is the seam.
//
// Drag-to-reorder: powered by @dnd-kit/sortable. Home is disabled as a
// drag source AND drop target (useSortable disabled) so it stays pinned
// at index 0 — matching its non-closeable, always-leftmost role. The
// PointerSensor's 5px activation distance keeps a plain click (focus) from
// being swallowed as a drag. Only pointer listeners are wired, not the
// keyboard sensor: dnd-kit's keyboard drag binds Enter/Space, which would
// collide with the tab's existing Enter/Space "focus this tab" handler, so
// tabs reorder by pointer only. The drag is pinned to the horizontal axis —
// see restrictToHorizontalAxis.js.

import { useCallback, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ICON, House, X } from '../icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useTabs } from './useTabs';
import { HOME_TAB_ID } from './storage';
import { getTitleKeyForPath } from './routeRegistry';
import { isDirty as isTabDirty } from './useDirtyTabs';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { restrictToHorizontalAxis } from './restrictToHorizontalAxis';
import './TabStrip.css';

// Home is pinned at index 0 (non-closeable, always leftmost), so it is
// neither a drag source nor a drop target. The object form is required:
// dnd-kit expands the boolean shorthand to `{draggable, droppable: false}`,
// which leaves Home a live drop target — and closestCenter always returns the
// nearest enabled container, so drags near Home would resolve onto it and die
// silently in reorderTabs' Home guard (useTabs.js).
const HOME_SORTABLE_DISABLED = { draggable: true, droppable: true };

// One tab. Wraps the tab node in useSortable so it can be dragged; the
// sortable layer only adds a ref, a transform style, and pointer listeners.
// We intentionally do NOT spread
// useSortable's `attributes`: they set role="button"/aria-roledescription
// ="sortable", which would both clash with the role="tab" semantics and
// (since keyboard drag isn't wired) mislead screen-reader users.
//
// `listeners` is spread unconditionally: useDraggable withholds them when
// disabled, so for Home it is
// undefined and the `disabled` option below stays the one place that
// decides whether a tab can be dragged.
function SortableTab({ tab, isActive, isHome, titleText, onClick, onAuxClick, onClose, closeLabel }) {
  const {
    setNodeRef,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: isHome ? HOME_SORTABLE_DISABLED : false });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Tooltip title={isDragging ? '' : titleText} describeChild>
      <div
        ref={setNodeRef}
        style={style}
        role="tab"
        aria-selected={isActive}
        tabIndex={0}
        className={[
          'tab-strip__tab',
          isActive ? 'is-active' : '',
          isHome ? 'is-home' : '',
          isDragging ? 'is-dragging' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => onClick(tab)}
        onAuxClick={(e) => onAuxClick(e, tab)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(tab);
          }
        }}
        {...listeners}
      >
        {isHome ? (
          <House size={ICON.md} weight={isActive ? 'fill' : 'regular'} />
        ) : (
          <span className="tab-strip__title">{titleText}</span>
        )}
        {!isHome && (
          <button
            type="button"
            className="tab-strip__close"
            aria-label={closeLabel}
            // Stop the pointer from reaching the sortable listeners so a
            // click on the ✕ closes instead of starting a drag.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => onClose(e, tab)}
          >
            <X size={ICON.xs} />
          </button>
        )}
      </div>
    </Tooltip>
  );
}

export default function TabStrip() {
  const { t } = useTranslation();
  const { tabs, activeId, focus, close, openTab, reorderTabs } = useTabs();
  const location = useLocation();
  const { confirm, dialogProps } = useConfirmDialog();

  // 5px activation distance: a plain click stays a click (focus), only a
  // deliberate drag past the threshold starts a reorder. PointerSensor
  // ignores non-primary buttons, so middle-click-to-close is unaffected.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // URL → activeId sync. Handles browser Back/Forward, pasted/shared
  // URLs, and direct address-bar typing. Three cases:
  //   1. URL matches the active tab — no-op
  //   2. URL matches some other open tab — focus it
  //   3. URL doesn't match any open tab — auto-open one (cap permitting)
  // The reverse direction (activeId → URL) is owned by TabsHost, so an
  // opener only changes the active tab and never touches the URL.
  useEffect(() => {
    const path = location.pathname;
    const match = tabs.find((tab) => tab.path === path);
    if (match) {
      if (match.id !== activeId) focus(match.id);
      return;
    }
    openTab(path);
    // We intentionally depend only on location.pathname — re-running on
    // every tabs/activeId change would create a feedback loop with focus().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleTabClick = useCallback(
    (tab) => {
      focus(tab.id);
      // URL follows activeId via TabsHost.
    },
    [focus],
  );

  // Guarded close: if the tab has unsaved edits (reported by
  // useDirtyTabReporter from useEditableGrid), prompt before discarding.
  // Returns a Promise that resolves once the close (or cancel) settles.
  const guardedClose = useCallback(
    async (tab) => {
      if (isTabDirty(tab.id)) {
        const ok = await confirm({
          title: t('common.unsavedChangesTitle'),
          message: t('tabs.unsavedEdits'),
          confirmLabel: t('common.discard'),
          variant: 'warning',
        });
        if (!ok) return;
      }
      close(tab.id);
    },
    [close, confirm, t],
  );

  // Middle-click closes the tab (Chrome convention). We use onAuxClick
  // because onClick doesn't fire for middle-button on most elements.
  const handleTabAuxClick = useCallback(
    (e, tab) => {
      if (e.button === 1) {
        e.preventDefault();
        guardedClose(tab);
      }
    },
    [guardedClose],
  );

  const handleCloseClick = useCallback(
    (e, tab) => {
      e.stopPropagation();
      guardedClose(tab);
    },
    [guardedClose],
  );

  // Commit the reorder once a drag settles. The Home-pinned policy lives in
  // reorderTabs (useTabs.js); dnd-kit keeps Home out of the collision
  // candidates because HOME_SORTABLE_DISABLED turns its droppable off
  // (DndContext only offers `droppableContainers.getEnabled()` to the
  // collision detector), so the reorder reaching that guard with Home is a
  // bug, not a routine drop.
  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (over && active.id !== over.id) {
        reorderTabs(active.id, over.id);
      }
    },
    [reorderTabs],
  );

  return (
    <div className="tab-strip" role="tablist" aria-label={t('tabs.ariaLabel')}>
      {/* closestCenter over the default rectIntersection: with a single
          row, "nearest tab centre" always resolves to a tab, so dropping
          past the last tab still reorders instead of resolving to no
          droppable at all. */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabs.map((tab) => tab.id)}
          strategy={horizontalListSortingStrategy}
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeId;
            const isHome = tab.id === HOME_TAB_ID;
            // A page's own title (setTitle: a PO number, "Items — <item>")
            // wins; otherwise the route's name, read fresh so a renamed or
            // re-languaged page renames its open tabs too.
            const titleKey = getTitleKeyForPath(tab.path);
            const titleText = tab.customTitle ?? t(titleKey, { defaultValue: titleKey });
            return (
              <SortableTab
                key={tab.id}
                tab={tab}
                isActive={isActive}
                isHome={isHome}
                titleText={titleText}
                onClick={handleTabClick}
                onAuxClick={handleTabAuxClick}
                onClose={handleCloseClick}
                closeLabel={t('tabs.close')}
              />
            );
          })}
        </SortableContext>
      </DndContext>

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
